import {
  getChannelApplyHostList,
  addChannelHost,
  removeChannelHost,
} from '_api/channel';
import { mapGetters, mapMutations } from 'vuex';
import {
  APPLY_HOST_OPERATION,
  HOST_OPERATION,
  getMinHostModeHeight,
  getMaxHostModeHeight,
  checkIsMinHostModeHeight,
} from '_lib/constants/host';
import EventBus from '_lib/EventBus';
import { playAudio } from '_lib/utils/utilsMedia';
import { TIP_SOUND_LIST } from '_lib/constants/config';

export default {
  computed: {
    isHostManager() {
      return this.$permission.judge('ManageHost', this.cur_channel_data);
    },
    ...mapGetters([
      'cur_channel_data',
      'applying_host_users',
      'channel_data',
      'user_info',
      'host_mode_height',
      'hosts',
      'tip_sound_control',
      'setting_data',
    ]),
  },
  mounted() {
    // 数据相关的事件
    EventBus.$on('Channel_Host:initData', this.handleInitData);

    EventBus.$on('Channel_Host:managerOperation', this.handleManagerOperation);

    EventBus.$on('SOCKET_HOST_UPDATE', this.handleUpdateHost);
    EventBus.$on('SOCKET_APPLY_HOST_UPDATE', this.handleUpdateApplyHost);

    // 组件相关的事件
    EventBus.$on('Host_Height_Change', this.handleHostHeightChange);
  },
  beforeDestroy() {
    EventBus.$off('Channel_Host:initData', this.handleInitData);

    EventBus.$off('Channel_Host:managerOperation', this.handleManagerOperation);
    EventBus.$off('SOCKET_HOST_UPDATE', this.handleUpdateHost);
    EventBus.$off('SOCKET_APPLY_HOST_UPDATE', this.handleUpdateApplyHost);

    EventBus.$off('Host_Height_Change', this.handleHostHeightChange);
  },
  methods: {
    /**
     * @description 进入语音频道成功之后，获取主持人模式相关数据
     */
    handleInitData() {
      if (this.cur_channel_data.host_mode) {
        this.handleInitChannelApplyHostList();
      } else {
        this.SET_APPLYING_HOST_USERS([]);
      }
      this.SET_IS_ME_APPLYING_HOST(false);
    },
    /**
     * @description 初始化频道申请上麦列表
     * @returns {void}
     */
    async handleInitChannelApplyHostList() {
      if (this.isHostManager) {
        const _channelId = this.cur_channel_data.channel_id;
        const res = await getChannelApplyHostList({
          channel_id: _channelId,
          room_id: this.cur_channel_data.room_id,
        });
        if (
          res.data.status === 'ok' &&
          _channelId === this.cur_channel_data.channel_id
        ) {
          const { apply_hosts = [] } = res.data.result;
          const apply_hosts_list = apply_hosts
            .map((hostId) => {
              const user = this.cur_channel_data.members?.find(
                (member) => member.user_id == hostId
              );
              if (!user) {
                return;
              }
              return user;
            })
            .filter((item) => item);
          this.SET_APPLYING_HOST_USERS(apply_hosts_list);
        }
      }
    },
    /**
     * @description 上/下麦的推送 频道内所有成员都会受到推送
     * @param {*} data
     * @returns {void}
     */
    handleUpdateHost(data) {
      const ws_data = data.data;
      const { channel_id, operation, user_id, force } = ws_data;

      if (channel_id != this.cur_channel_data.channel_id) return;

      const isMe = user_id == this.user_info.user_id;

      if (operation === HOST_OPERATION.ADD) {
        if (!this.hosts.includes(user_id)) {
          this.SET_CHANNEL_HOSTS(this.hosts.concat(user_id));
        }

        if (isMe) {
          if (force) {
            this.playTipSound(13);
          }
          this.SET_IS_ME_APPLYING_HOST(false);

          this.$rtc.handleHostModeBannedChange(false);
        } else {
          if (this.isHostManager) {
            // 更新申请列表
            this.SET_APPLYING_HOST_USERS(
              this.applying_host_users.filter((user) => user.user_id != user_id)
            );
          }
        }
      } else if (operation === HOST_OPERATION.REMOVE) {
        this.SET_CHANNEL_HOSTS(
          this.hosts.filter((_user_id) => _user_id != user_id)
        );

        if (isMe) {
          if (force) {
            this.playTipSound(15);
          }
          this.SET_IS_ME_APPLYING_HOST(false);

          this.$rtc.handleHostModeBannedChange(true);
        } else {
          const user = this.applying_host_users.find(
            ({ user_id: _user_id }) => _user_id == user_id
          );

          if (!user) {
            return;
          }
        }
      }
    },
    /**
     * @description 申请上麦的推送 自己和管理员都会受到这个推送
     * @param {*} data
     * @returns {void}
     */
    handleUpdateApplyHost(data) {
      const ws_data = data.data;
      const { channel_id, operation, user_id } = ws_data;

      if (channel_id != this.channel_data.channel_id) return;

      const isMe = user_id == this.user_info.user_id;

      // 对于自己的操作，需要更新自己的状态
      if (isMe) {
        if (operation === APPLY_HOST_OPERATION.ADD) {
          this.SET_IS_ME_APPLYING_HOST(true);
        } else if (operation === APPLY_HOST_OPERATION.REMOVE) {
          this.SET_IS_ME_APPLYING_HOST(false);
        }
        // 对于管理员的操作，需要更新管理员的举手列表
      } else {
        if (operation === APPLY_HOST_OPERATION.ADD) {
          if (
            this.applying_host_users.some((user) => user.user_id == user_id)
          ) {
            return;
          }
          const user = this.channel_data.members?.find(
            ({ user_id: _user_id }) => _user_id == user_id
          );

          if (!user) {
            return;
          }

          this.SET_APPLYING_HOST_USERS(this.applying_host_users.concat(user));
          this.playTipSound(14);
        } else if (operation === APPLY_HOST_OPERATION.REMOVE) {
          const newList = this.applying_host_users.filter(
            ({ user_id: _user_id }) => _user_id != user_id
          );
          this.SET_APPLYING_HOST_USERS(newList);
        }
      }
    },
    /**
     * @description 上麦组件高度变化 当高度变化时，会触发这个方法
     * @description 如果newHeight为undefined，则说明是点击主持人组件的上或者下
     * @param {number | undefined} newHeight 上麦组件高度， 或者是组件是否向上移动
     * @returns {void}
     */
    handleHostHeightChange(newHeight) {
      if (typeof newHeight === 'number') {
        this.SET_HOST_MODE_HEIGHT(newHeight);
      } else {
        const isMinHeight = checkIsMinHostModeHeight(
          this.host_mode_height,
          this.showScreenShareDom
        );
        if (isMinHeight) {
          this.SET_HOST_MODE_HEIGHT(getMaxHostModeHeight());
        } else {
          this.SET_HOST_MODE_HEIGHT(getMinHostModeHeight());
        }
      }
    },
    /**
     * 管理员操作上麦/下麦
     * @param {string} userId 用户信息
     * @param {boolean} isAdd 上下麦
     * @returns {void}
     */
    handleManagerOperation(userId, isAdd) {
      const api = isAdd ? addChannelHost : removeChannelHost;
      api(
        {},
        {
          room_id: this.cur_room_data.room_id,
          channel_id: this.cur_channel_data.channel_id,
          to_user_id: userId,
        }
      );
    },
    playTipSound(index) {
      const item = TIP_SOUND_LIST[index]
      this.tip_sound_control(index) &&
        playAudio(item.path, this.setting_data.tip_sound_volume);
    },
    ...mapMutations([
      'SET_APPLYING_HOST_USERS',
      'SET_IS_ME_APPLYING_HOST',
      'SET_HOST_MODE_HEIGHT',
      'SET_CHANNEL_HOSTS',
      'SET_BGM_STATE',
    ]),
  },
};
